import type {ReactNode} from 'react'
import {TarjetaUbicar, type DestinoHoja} from '@/components/bateria/cola/tarjeta-ubicar'
import {TarjetaMarca} from '@/components/bateria/cola/tarjeta-marca'
import {TarjetaDeFicha} from '@/components/bateria/cola/tarjeta-de-ficha'
import type {FocoEscritura} from '@/components/bateria/cola/tarjeta-campo'
import type {GestoEnCurso} from '@/components/bateria/cola/gesto-en-curso'
import {
  casillasGuardadasDe,
  claveDeRef,
  gestoGuardadoDe,
  seleccionDeCampo,
  seleccionDeMarca,
  type CandidatoIdentidad,
  type DecisionLocal,
  type PaqueteDestino,
  type SeccionClave,
  type TarjetaCola,
} from '@/lib/bateria-cola'

/** Despacho por `tipo`, sin estado propio. */
export function TarjetaEnCurso({
  tarjeta,
  seccion,
  posicion,
  decision,
  gesto,
  businessId,
  evaluacionId,
  opciones,
  paquetes,
  puedeDecidir,
  ubicar,
  escritura,
  nav,
  onElegirMarca,
  onTachonSinRespuesta,
  onElegirCampo,
  onSenalarCasilla,
  clicSenala,
}: {
  tarjeta: TarjetaCola
  seccion: SeccionClave
  posicion: ReactNode
  /** De esta sesión o del servidor. */
  decision: DecisionLocal | undefined
  gesto: GestoEnCurso
  businessId: string
  evaluacionId: string
  /** Del catálogo de la ficha; vacío para los otros tipos. */
  opciones: string[]
  paquetes: PaqueteDestino[]
  /** Viaja como prop y no se recalcula por tarjeta: es la misma vara que apaga las teclas. */
  puedeDecidir: boolean
  ubicar: {
    destino: DestinoHoja | null
    candidatos: CandidatoIdentidad[]
    buscandoCandidatos: boolean
    descarteArmado: boolean
    buscando: boolean
    pendiente: boolean
    onElegirCandidato: (i: number) => void
    onAsignar: (elegido: DestinoHoja) => void
    onDescartar: () => void
    onBuscar: (abierto: boolean) => void
  }
  escritura: {
    borrador: string
    foco: FocoEscritura
    onEscribir: () => void
    onCambiar: (valor: string) => void
    onSoltarFoco: () => void
  }
  nav: {onDespues: () => void; onAtras: () => void; onAdelante: () => void}
  onElegirMarca: (valor: string | null) => void
  onTachonSinRespuesta: () => void
  onElegirCampo: (valor: string | null) => void
  onSenalarCasilla: (valor: string) => void
  clicSenala: boolean
}) {
  if (tarjeta.tipo === 'ubicar') {
    return (
      <TarjetaUbicar
        // Sin `key`, el estado interno del buscador manual sobrevive al cambio de hoja.
        key={tarjeta.ref}
        tarjeta={tarjeta}
        paquetes={paquetes}
        posicion={posicion}
        seleccion={ubicar.destino}
        candidatos={ubicar.candidatos}
        buscandoCandidatos={ubicar.buscandoCandidatos}
        onElegirCandidato={ubicar.onElegirCandidato}
        onAsignar={ubicar.onAsignar}
        onDescartar={ubicar.onDescartar}
        descarteArmado={ubicar.descarteArmado}
        onDespues={nav.onDespues}
        onAtras={nav.onAtras}
        onAdelante={nav.onAdelante}
        buscando={ubicar.buscando}
        onBuscar={ubicar.onBuscar}
        pendiente={ubicar.pendiente}
        puedeDecidir={puedeDecidir}
      />
    )
  }
  if (tarjeta.tipo === 'marca') {
    return (
      <TarjetaMarca
        tarjeta={tarjeta}
        seccion={seccion}
        posicion={posicion}
        seleccion={seleccionDeMarca(decision)}
        gesto={gesto}
        casillasGuardadas={casillasGuardadasDe(decision)}
        businessId={businessId}
        evaluacionId={evaluacionId}
        puedeDecidir={puedeDecidir}
        onElegir={onElegirMarca}
        onTachonSinRespuesta={onTachonSinRespuesta}
        onDespues={nav.onDespues}
        onAtras={nav.onAtras}
        onAdelante={nav.onAdelante}
      />
    )
  }
  return (
    <TarjetaDeFicha
      tarjeta={tarjeta}
      opciones={opciones}
      posicion={posicion}
      seleccion={seleccionDeCampo(decision)}
      gestos={{
        gesto,
        gestoGuardado: gestoGuardadoDe(decision),
        casillasGuardadas: casillasGuardadasDe(decision),
        onSenalar: onSenalarCasilla,
        clicSenala,
      }}
      borrador={escritura.borrador}
      foco={escritura.foco}
      puedeDecidir={puedeDecidir}
      onElegir={onElegirCampo}
      onEscribir={escritura.onEscribir}
      onCambiar={escritura.onCambiar}
      onSoltarFoco={escritura.onSoltarFoco}
      onDespues={nav.onDespues}
      onAtras={nav.onAtras}
      onAdelante={nav.onAdelante}
      businessId={businessId}
      evaluacionId={evaluacionId}
    />
  )
}

export function campoDeTarjeta(tarjeta: TarjetaCola): string {
  return claveDeRef(tarjeta.ref) ?? ''
}
